import { useId, useState } from 'react';
import { CaretDownIcon, CaretRightIcon, PlusIcon } from '@phosphor-icons/react';
import { Num, T, useGT } from 'gt-react';
import {
  WORKBOOK_MAX_SCHEMA_GROUP_MEMBERS,
  WORKBOOK_MIN_SCHEMA_GROUP_MEMBERS,
  type WorkbookCatalogEntry,
  type WorkbookDefinition,
  type WorkbookSource,
} from '@erp/shared-types';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { catalogSource, matchingGroupMembers } from './workbookCatalog';
import { memberTitle } from './workbookAddDataItems';
import { tableNames, unusedName } from './workbookNames';
import {
  schemaGroupMembers,
  schemaGroupMembersValid,
  type SchemaGroup,
} from './workbookSchemaGroup';
import type { GroupChoice } from './useGroupChoices';
import { AddedButton } from './WorkbookAddedButton';
import { isDuplicateSource } from './workbookSourceIdentity';

/** `ids` with `id` left out, or let back in when it was. */
function toggled(ids: ReadonlySet<string>, id: string): ReadonlySet<string> {
  const next = new Set(ids);
  if (!next.delete(id)) next.add(id);
  return next;
}

/**
 * Data Sources that share a schema, in the Add data panel: the schema is the
 * row, its Data Sources sit below it. By default they are combined into one
 * table (each can be left out); with that off they are added one by one.
 */
export function SchemaGroupRow({
  group,
  choice,
  onChoice,
  catalog,
  query,
  definition,
  onSource,
}: {
  group: SchemaGroup;
  choice: GroupChoice;
  onChoice: (patch: Partial<GroupChoice>) => void;
  /** The catalog's Data Sources, to add a member as the catalog names it. */
  catalog: readonly WorkbookCatalogEntry[];
  /** The catalog search; only the members it matches are listed. */
  query: string;
  /** The Workbook's tables, so a new table is named apart from them. */
  definition: Pick<WorkbookDefinition, 'sources' | 'sheets'>;
  /** Whether the source was added. */
  onSource: (source: WorkbookSource) => boolean;
}) {
  const gt = useGT();
  // The schema's complete membership, not the capped catalog's share of it.
  const links = group.members;
  const {
    auto,
    setAuto,
    excluded,
    setExcluded,
    labelColumn,
    setLabelColumn,
    members,
    blocked,
  } = useCombinedChoice(links, choice, onChoice);
  const { combine } = choice;
  const shown = links && matchingGroupMembers(group, links, catalog, query);
  const added = new Set(
    definition.sources.flatMap((source) =>
      source.kind === 'data-source' ? [source.linkId] : [],
    ),
  );
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2 rounded-md border border-input px-2 py-1.5">
        <span className="min-w-0 text-sm font-medium">
          {group.schemaName}
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            <T context="workbook schema group count">
              <Num>{group.linkCount}</Num> data sources
            </T>
          </span>
        </span>
        <label className="flex shrink-0 items-center gap-2 text-xs">
          <Switch
            checked={combine}
            onCheckedChange={(next) => onChoice({ combine: next })}
          />
          <T context="workbook schema group combine">Combine into one table</T>
        </label>
      </div>
      <div className="ml-3 space-y-2 border-l border-input pl-3">
        {combine ? (
          <>
            <MemberChecklist
              members={shown}
              total={links.length}
              selected={
                links.filter((link) => !choice.excluded.has(link.id)).length
              }
              searching={query.trim() !== ''}
              titleOf={(link) => memberTitle(link, group.schemaName, catalog)}
              excluded={excluded}
              onToggle={(id) => setExcluded(toggled(choice.excluded, id))}
            />
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2 text-xs">
                <Switch
                  checked={auto}
                  disabled={auto && !links}
                  onCheckedChange={setAuto}
                />
                <T context="workbook schema group auto include">
                  Include new data sources automatically
                </T>
              </label>
              <Input
                className="h-7 w-56 text-xs"
                aria-label={gt('Label column', {
                  context: 'workbook schema group label',
                })}
                placeholder={gt('Label column (optional), e.g. Source', {
                  context: 'workbook schema group label',
                })}
                value={labelColumn}
                onChange={(event) => setLabelColumn(event.target.value)}
              />
            </div>
            {!auto && !blocked && <PickRangeHint members={members} />}
            {isDuplicateSource(
              definition.sources,
              combinedSource(group, definition, members, labelColumn),
            ) ? (
              <AddedButton />
            ) : (
              <Button
                size="sm"
                disabled={blocked || !schemaGroupMembersValid(members)}
                onClick={() => {
                  if (blocked) return;
                  const done = onSource(
                    combinedSource(group, definition, members, labelColumn),
                  );
                  if (!done) return;
                  setExcluded(new Set());
                  setLabelColumn('');
                }}
              >
                <PlusIcon />
                <T context="workbook add schema group input">
                  Add combined table
                </T>
              </Button>
            )}
          </>
        ) : (
          <SeparateMemberButtons
            members={shown}
            titleOf={(link) => memberTitle(link, group.schemaName, catalog)}
            added={added}
            catalog={catalog}
            onSource={onSource}
          />
        )}
      </div>
    </div>
  );
}

/** One button per member not yet in the Workbook, adding it as the catalog names it. */
function SeparateMemberButtons({
  members,
  titleOf,
  added,
  catalog,
  onSource,
}: {
  members: readonly { id: string; name: string }[] | undefined;
  /** What a person calls the member, shown in place of its table name. */
  titleOf: (link: { id: string; name: string }) => string;
  added: ReadonlySet<string>;
  catalog: readonly WorkbookCatalogEntry[];
  onSource: (source: WorkbookSource) => boolean;
}) {
  return (
    <div className="grid gap-2">
      {members?.flatMap((link) => {
        if (added.has(link.id)) return [];
        const entry = catalog.find((item) => item.id === link.id);
        return [
          <Button
            variant="outline"
            className="justify-start"
            key={link.id}
            onClick={() =>
              onSource(
                entry
                  ? catalogSource(entry)
                  : {
                      id: crypto.randomUUID(),
                      kind: 'data-source',
                      folder: '',
                      name: link.name,
                      linkId: link.id,
                    },
              )
            }
          >
            <PlusIcon />
            <span className="text-left">{titleOf(link)}</span>
          </Button>,
        ];
      })}
    </div>
  );
}

/**
 * The members the search shows, each a checkbox: unchecked ones stay out of
 * the combined table. Collapsed to how many are selected until opened, or
 * while a search narrows them.
 */
function MemberChecklist({
  members,
  total,
  selected,
  searching,
  titleOf,
  excluded,
  onToggle,
}: {
  members: readonly { id: string; name: string }[] | undefined;
  /** How many members the schema has, shown or not, and how many are in. */
  total: number;
  selected: number;
  searching: boolean;
  /** What a person calls the member, shown in place of its table name. */
  titleOf: (link: { id: string; name: string }) => string;
  excluded: ReadonlySet<string>;
  onToggle: (id: string) => void;
}) {
  const [opened, setOpened] = useState(false);
  const listId = useId();
  const open = opened || searching;
  return (
    <div className="space-y-1">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        disabled={searching}
        onClick={() => setOpened((current) => !current)}
        className="flex items-center gap-1 rounded-sm text-xs text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:hover:text-muted-foreground"
      >
        {open ? (
          <CaretDownIcon className="size-3.5" aria-hidden />
        ) : (
          <CaretRightIcon className="size-3.5" aria-hidden />
        )}
        <span className="tabular-nums">
          <T context="workbook schema group: how many of its data sources are selected">
            <Num>{selected}</Num> of <Num>{total}</Num> selected
          </T>
        </span>
      </button>
      {open && (
        <MemberList
          id={listId}
          members={members}
          titleOf={titleOf}
          excluded={excluded}
          onToggle={onToggle}
        />
      )}
    </div>
  );
}

function MemberList({
  id,
  members,
  titleOf,
  excluded,
  onToggle,
}: {
  id: string;
  members: readonly { id: string; name: string }[] | undefined;
  titleOf: (link: { id: string; name: string }) => string;
  excluded: ReadonlySet<string>;
  onToggle: (id: string) => void;
}) {
  return (
    <ul
      id={id}
      className="grid gap-1 pl-4 text-xs @lg:grid-cols-2 @3xl:grid-cols-3"
    >
      {members?.map((link) => (
        <li key={link.id}>
          <label className="flex items-center gap-2">
            <Checkbox
              checked={!excluded.has(link.id)}
              onCheckedChange={() => onToggle(link.id)}
            />
            {titleOf(link)}
          </label>
        </li>
      ))}
    </ul>
  );
}

/** Explains the member count a fixed list needs, once it is out of range. */
function PickRangeHint({
  members,
}: {
  members: Parameters<typeof schemaGroupMembersValid>[0];
}) {
  if (schemaGroupMembersValid(members)) return null;
  return (
    <p className="text-xs text-muted-foreground">
      <T context="workbook schema group pick range">
        Pick between <Num>{WORKBOOK_MIN_SCHEMA_GROUP_MEMBERS}</Num> and{' '}
        <Num>{WORKBOOK_MAX_SCHEMA_GROUP_MEMBERS}</Num> data sources.
      </T>
    </p>
  );
}

/** The Source that combines a schema's Data Sources into one table. */
function combinedSource(
  group: SchemaGroup,
  definition: Pick<WorkbookDefinition, 'sources' | 'sheets'>,
  members: ReturnType<typeof schemaGroupMembers>,
  labelColumn: string,
): WorkbookSource {
  const label = labelColumn.trim();
  return {
    id: crypto.randomUUID(),
    kind: 'schema-group',
    folder: '',
    name: unusedName(tableNames(definition), group.schemaName),
    schemaId: group.schemaId,
    members,
    ...(label ? { labelColumn: label } : {}),
  };
}

/** The user's choice for the combined table, and whether Add must wait or has nothing to add. */
function useCombinedChoice(
  links: readonly { id: string }[] | undefined,
  { auto, excluded, labelColumn }: GroupChoice,
  onChoice: (patch: Partial<GroupChoice>) => void,
) {
  // A fixed list can't be built until the complete membership is read, so Add
  // waits rather than falling back to "all".
  const members = schemaGroupMembers(links ?? [], [...excluded], auto);
  const waiting = !auto && !links;
  const nothingChosen = !!links && links.every((link) => excluded.has(link.id));
  return {
    auto,
    setAuto: (next: boolean) => onChoice({ auto: next }),
    excluded,
    setExcluded: (next: ReadonlySet<string>) => onChoice({ excluded: next }),
    labelColumn,
    setLabelColumn: (next: string) => onChoice({ labelColumn: next }),
    members,
    blocked: waiting || nothingChosen,
  };
}
